import React, { useState, useEffect } from 'react';
import Sidebar from '../Sidebar';
import { ClipboardList, Upload } from 'lucide-react';
import ModuloHeader from '../ModuloHeader';
import { interfaceMinima } from '../../utils/perfisRestritos';
import ProjetosInventario from './ProjetosInventario';
import ProjetosEnvio from './ProjetosEnvio';

// ---------------------------------------------------------------------------
// Módulo PROJETOS — Inventário Executivo do Sr. Marco (CEO)
// ---------------------------------------------------------------------------
// Dá link permanente no domínio corporativo ao inventário de assuntos em
// acompanhamento que o Sr. Marco mantinha como arquivo solto no computador.
//
// O portal é VITRINE, não dono do conteúdo: o HTML é produzido por um processo
// que roda fora daqui (o próprio arquivo se declara "estrutura congelada"), e o
// módulo guarda, versiona e serve — sem opinar sobre o que tem dentro.
//
// ⚠️ SEM TELA DE "VISÃO GERAL". Os outros hubs abrem num painel de cards porque
//    reúnem vários submódulos; aqui existe UMA coisa para ver. Um painel
//    intermediário só acrescentaria um clique entre o CEO e o conteúdo dele —
//    e o pedido era justamente um link que abre o inventário.
//
// ⚠️ ACESSO POR USUÁRIO, NÃO POR PERFIL (exceção deliberada ao padrão do
//    portal): o perfil dele, `Diretoria`, tem 8 pessoas. Regra em
//    src/utils/acessoProjetos.js (menu/rota) e, de verdade, no backend
//    server/projetos/acesso-projetos.js.

const COR = '#7c3aed';       // violeta — identidade do módulo (ARQUITETURA › Paleta dos módulos)
const COR_CLARA = '#ede9fe';

const abas = [
    { id: 'inventario', label: 'Inventário', icon: ClipboardList },
    { id: 'envio', label: 'Publicar versão', icon: Upload },
];

const Projetos = () => {
    const [isMobile] = useState(window.innerWidth <= 768);

    // Interface enxuta (sem menu lateral, sino nem agenda) para quem enxerga uma
    // tela só — hoje o perfil `Presidencia`. Regra derivada, não hardcoded:
    // ver utils/perfisRestritos.js › interfaceMinima.
    const [enxuto] = useState(() => {
        try {
            const s = localStorage.getItem('user');
            return s ? interfaceMinima(JSON.parse(s).perfil) : false;
        } catch { return false; }
    });

    // Menu RECOLHIDO por padrão neste módulo (e não só no celular, como nos
    // demais). Dois motivos: o dono do inventário tem perfil `Presidencia`, cujo
    // menu inteiro é UM item — expandido, ocupa 260px para não dizer nada; e o
    // conteúdo é uma página cheia, que agradece a largura. Continua expansível
    // pelo botão, para quem entra como Admin e quer navegar.
    const [isCollapsed, setIsCollapsed] = useState(true);

    // Abre direto no inventário: é o que o link permanente promete mostrar.
    const [activeTab, setActiveTab] = useState('inventario');

    // Muda a cada publicação para forçar o inventário a recarregar quando o
    // usuário volta para a aba de leitura (senão ele mostraria a versão antiga).
    const [recarga, setRecarga] = useState(0);

    useEffect(() => {
        const handleResize = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Sem menu lateral não há faixa a compensar — a página começa na borda.
    const getMarginLeft = () => {
        if (enxuto || window.innerWidth <= 768) return '0px';
        return isCollapsed ? '80px' : '260px';
    };

    // A aba de publicação usa a caixa branca padrão dos módulos. A do inventário
    // NÃO: o conteúdo é uma página inteira, e a moldura (padding + fundo + borda)
    // só roubaria área de leitura — o próprio componente já traz a sua borda.
    const ehInventario = activeTab === 'inventario';
    const contentBoxStyle = ehInventario ? { width: '100%' } : {
        backgroundColor: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0',
        padding: '28px', minHeight: '70vh', width: '100%', boxSizing: 'border-box',
    };

    return (
        <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#faf8ff' }}>
            {/* Sem menu lateral para quem enxerga UMA tela só: seria um item que
                leva à página onde a pessoa já está. Quem entra como Admin — para
                monitorar ou publicar — continua com o menu inteiro. */}
            {!enxuto && (
                <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} mostrarBotaoMobile={false} />
            )}
            <div style={{ flex: 1, marginLeft: getMarginLeft(), transition: 'margin-left 0.3s ease' }}>

                <ModuloHeader
                    eyebrow="Módulo"
                    titulo="Projetos"
                    cor={COR}
                    onAbrirMenu={() => setIsCollapsed(false)}
                    isMobile={isMobile}
                />

                {/* No inventário a área útil é o que importa: margens menores e sem
                    teto de largura, para a página do Sr. Marco respirar como num
                    navegador. Na publicação, mantém o enquadramento dos módulos. */}
                <div style={{
                    padding: ehInventario ? '16px 20px 20px' : '32px 40px 40px',
                    maxWidth: ehInventario ? 'none' : '1600px',
                    margin: '0 auto', width: '100%', boxSizing: 'border-box',
                }}>

                    {/* BARRA DE NAVEGAÇÃO — duas telas: ler e publicar */}
                    <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: '10px',
                        marginBottom: ehInventario ? '14px' : '26px',
                        backgroundColor: '#fff', padding: '8px', borderRadius: '12px',
                        border: '1px solid #e2e8f0', width: '100%',
                    }}>
                        {abas.map((aba) => (
                            <button
                                key={aba.id}
                                onClick={() => setActiveTab(aba.id)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '8px',
                                    padding: '10px 20px', borderRadius: '8px', fontWeight: 600,
                                    cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap',
                                    border: activeTab === aba.id ? `2px solid ${COR}` : '2px solid transparent',
                                    backgroundColor: activeTab === aba.id ? COR_CLARA : 'transparent',
                                    color: activeTab === aba.id ? COR : '#64748b',
                                }}
                            >
                                <aba.icon size={18} /> {aba.label}
                            </button>
                        ))}
                    </div>

                    <div style={contentBoxStyle}>
                        {activeTab === 'inventario' ? (
                            <ProjetosInventario
                                key={recarga}
                                podeEnviar
                                onIrParaEnvio={() => setActiveTab('envio')}
                            />
                        ) : (
                            <ProjetosEnvio onPublicado={() => setRecarga((n) => n + 1)} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Projetos;
